<template>
	<v-card class="directory-page pt-5" flat>
		<PageBlocked v-if="isBlocked" :blockRole="blockRole" />
		<div v-else-if="isBlocked !== null">
			<v-skeleton-loader v-if="isDirectoryEmpty" v-bind="attrs" type="card" />
			<v-card-text v-else>
				<div :style="{paddingTop: customFlag ? '32px':'12px'}" class="directory-page__section">
					<div class="directory-page__info-wrapper">
            <SponsorBadge
                v-if="customFlag"
                :text="customFlagName"
                :top="'0px'"
                :right="'0px'"
                :iconSize="10"
                :iconColor="'#ffffff'"
                :background="customBackgroundColor"
            />
						<Avatar
							:avatar="avatarPath"
							:size="120"
							border-radius="square"
							class="directory-page__avatar mr-3"
						/>

						<div class="directory-page__info">
							<h1 class="directory-page__info-username">{{ userName }}</h1>
							<div class="directory-page__info-description">
								<span v-if="companyName" class="directory-page__info-company">
									{{ companyName }}
								</span>
								<span v-if="heldPosition" class="directory-page__info-position">
									{{ heldPosition }}
								</span>
							</div>
							<span v-if="location" class="directory-page__info-location">
								<span>{{ location }}</span>
							</span>
						</div>
					</div>
				</div>
				<div
					v-if="subDir.value.count_items > 0"
					v-for="subDir in subDirectories"
					:key="subDir.field"
					class="directory-page__section"
				>
					<div class="directory-page__section-title mb-3">
						employment {{ subDir.section }}
					</div>
					<router-link
						class="text-decoration-none"
						:to="{
							name: 'sub-directory-by-id',
							params: { id: directoryId },
							query: { category_id: subDir.value.directory_category_id },
						}"
					>
						<div class="directory-page__section__employment-container">
							<span class="directory-page__section__employment-container__title">
								{{ subDir.title }}
							</span>
							<div
								class="directory-page__section__employment-container__count-wrapper"
							>
								<span class="directory-page__section__employment-container__count">
									{{ subDir.value.count_items }}
								</span>
								<Icon type="chevron_right" :size="24" color="#9b9b9b" />
							</div>
						</div>
					</router-link>
				</div>

				<div
					v-for="(section, index) in allowSections"
					:key="index"
					class="directory-page__section"
				>
					<h3 class="directory-page__section-title mb-3">
						{{ section[0].section }}
					</h3>
					<div v-for="(field, fieldIndex) in section" :key="field.field">
						<div v-if="field.type === 'email'" class="directory-page__section__mail">
							<Icon type="email" color="var(--button-icon-tint)" :pr="8" :size="24" />
							<a
								@click="toggleDirectoryEmailClick(field.value)"
								:href="'mailto:' + field.value"
								style="text-decoration: none; color: var(--link)"
							>
								{{ field.value }}
							</a>
						</div>
						<div v-else-if="socialFieldTypes.some((type) => type === field.type)">
							<a
								:href="field.value"
								target="_blank"
								class="directory-page__network-link"
							>
								<div class="directory-page__section__network">
									<Icon
										:size="24"
										:type="networkFieldType(field.type)"
										color="var(--button-icon-tint)"
									/>
									<span
										@click="toggleDirectorySocialClick(field.value)"
										class="directory-page__network-link-text ml-2"
									>
										{{ field.value }}
									</span>
								</div>
							</a>
						</div>
						<div
							v-else-if="field.type === 'phone'"
							class="directory-page__section__phone"
						>
							<Icon type="phone" color="var(--button-icon-tint)" :pr="8" :size="24" />
							<a
								@click="copyPhone(field.value)"
								:href="null"
								style="text-decoration: none; color: var(--link)"
							>
								{{ field.value }}
							</a>
						</div>
						<v-card-text v-else-if="field.type === 'html'" v-html="field.value" />
						<div
							class="directory-page__section__text-wrapper"
							:class="{ 'mb-0': fieldIndex === section.length - 1 }"
							v-else-if="field.section !== 'Network'"
						>
							<span class="directory-page__section-label"> {{ field.title }}: </span>
							<span class="directory-page__section-text">
								{{ field.value }}
							</span>
						</div>
					</div>
				</div>
			</v-card-text>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import PageBlocked from '@/components/PageBlocked'
import ProfileBadge from '@/components/ProfileBadge.vue'
import Block from '@/mixins/Block.js'
import Dwh from '@/mixins/Dwh'
import SponsorBadge from "@/components/SponsorBadge.vue";

export default {
	name: 'DirectoryPage',
	mixins: [Block, Dwh],
	components: {
    SponsorBadge,
		PageBlocked,
		Avatar,
		ProfileBadge,
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),
		...mapGetters('directory', ['pageDirectory', 'directory']),
		...mapGetters('event', ['attendee']),
		...mapGetters('app', ['isShowAlert', 'alertMessage']),
		directoryId() {
			return this.directory?.directory_id
		},
		isDirectoryEmpty() {
			const { directory_id: directoryId } = this.directory || {}
			return directoryId !== +this.$route.params.id
		},
		customFlagName() {
			const { custom_flag_name: value } = this.directory || {}
			return value
		},
		allowSections() {
			return this.directory.public
				.filter((field) => field.value && field.type !== 'sub_directory')
				.reduce((acc, current) => {
					if (!acc.length || acc[acc.length - 1][0].section !== current.section) {
						acc.push([current])
					} else {
						acc[acc.length - 1].push(current)
					}
					return acc
				}, [])
		},

		subDirectories() {
			return (this.directory.public || [])
				.filter((f) => f.type === 'sub_directory')
				.map((f) => {
					let parsedValue = {}
					try {
						parsedValue = JSON.parse(f.value)
					} catch (e) {
						console.warn('Error parsing sub_directory.value:', f.value)
					}
					return {
						...f,
						value: parsedValue,
					}
				})
		},
		location() {
			const { directory_forth_row: location } = this.directory || {}
			return location
		},
		userName() {
			const { directory_first_row: firstName } = this.directory || {}
			return `${firstName}`
		},
		avatarPath() {
			const { directory_logo: logo } = this.directory || {}
			return logo
		},
		heldPosition() {
			const { directory_third_row: heldPosition } = this.directory || {}
			return heldPosition
		},
		companyName() {
			const { directory_second_row: company } = this.directory || {}
			return company
		},
		customBackgroundColor() {
			const { custom_flag_color: value } = this.directory || {}
			return value
		},
		customFlag() {
			const { custom_flag: value } = this.directory || {}
			return value
		},
		userId() {
			return this.profile?.user_id || null
		},
	},
	methods: {
		...mapActions('app', ['showAlert', 'hideAlert', 'setPageTitle']),
		...mapActions('directory', ['getDirectory', 'clearDirectory']),
		toggleDirectoryEmailClick(text) {
			this.sendTrigger({
				category: 'Content',
				action: 'directory_email_click',
				module: 'Directories',
				chapter_id: this.userId,
				data: text,
			})
		},
		toggleDirectoryPhoneClick(text) {
			this.sendTrigger({
				category: 'Content',
				action: 'directory_phone_click',
				module: 'Directories',
				chapter_id: this.userId,
				data: text,
			})
		},
		toggleDirectorySocialClick(text) {
			this.sendTrigger({
				category: 'Content',
				action: 'directory_social_click',
				module: 'Directories',
				chapter_id: this.userId,
				data: text,
			})
		},

		copyPhone(value) {
			this.toggleDirectoryPhoneClick(value)
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '')
				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)
					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
		networkFieldType(type) {
			return type === 'url' ? 'link' : type
		},
	},
	data() {
		return {
			attrs: {
				elevation: 0,
			},
			socialFieldTypes: ['linkedin', 'facebook', 'twitter', 'custom_url', 'url'],
			pageBlockedName: 'directory',
		}
	},
	mounted() {
		this.setPageTitle('Member Profile')
	},
	created() {
		if (this.$route.params.id && (!this.isBlocked || this.isBlocked === null)) {
			this.getDirectory(+this.$route.params.id)
		}
	},
	beforeDestroy() {
		this.clearDirectory()
		this.setPageTitle('')
	},
}
</script>

<style lang="scss">
.directory-page {
	background-color: transparent !important;

	&__section {
		max-width: 555px;
		border-radius: 8px !important;
		margin-bottom: 8px;
		padding: 12px;
		background-color: #ffffff;
		font-family: 'Inter', sans-serif;
		border: 1px solid #e2e2e2;
    position: relative;
		&__employment-container {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 12px;
			gap: 4px;
			border-radius: 8px;
			background-color: #f3f3f6;
			cursor: pointer;
			&__count-wrapper {
				display: flex;
				align-items: center;
				gap: 4px;
			}
			&__title {
				font-family: 'Inter', sans-serif;
				font-weight: 400;
				font-size: 14px;
				line-height: 100%;
				letter-spacing: 0;
				color: #4a4a4a;
			}
			&__count {
				font-family: 'Inter', sans-serif;
				font-weight: 400;
				font-size: 14px;
				line-height: 100%;
				letter-spacing: 0;
				text-align: right;
				color: #4a4a4a;
			}
		}
		&__mail {
			display: flex;
			padding: 12px;
			background-color: #f3f3f6;
			border-radius: 8px;
		}
		&__network {
			display: flex;
			align-items: center;
			padding: 12px;
			background-color: #f3f3f6;
			border-radius: 8px;
			margin-top: 8px;
		}
		&__phone {
			display: flex;
			align-items: center;
			padding: 12px;
			background-color: #f3f3f6;
			border-radius: 8px;
			margin-bottom: 8px;
		}
		&-title {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--accent-color);
		}
		&__text-wrapper {
			margin-bottom: 12px;
			&.mb-0 {
				margin-bottom: 0;
			}
		}

		&-label {
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--accent-color);
		}

		&-text {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}

	&__network {
		&-link {
			text-decoration: none;
			display: block;

			&-text {
				font-size: 14px;
				line-height: 17.07px;
				color: var(--link);
				width: 100%;
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
			}
		}

		&-title {
			font-size: 14px;
			font-weight: 600;
			line-height: 22px;
			color: #4a4a4a;
			margin-bottom: 4px;
		}
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: 8px;
		justify-content: center;
		font-family: 'Inter', sans-serif;
		width: 100%;
		&-description {
			display: flex;
			flex-direction: column;
			gap: 4px;
		}

		&-wrapper {
			display: flex;
			flex-wrap: nowrap;
		}

		&-username {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 24px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
		}
		&-location,
		&-company,
		&-position {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
			width: 100%;
			white-space: normal;
			word-break: break-word;
		}
	}
}
</style>
